// src/components/PromoCard/PromoCard.jsx

import React, {useEffect, useState} from "react";
import {UploadOutlined, CopyOutlined, CheckOutlined} from "@ant-design/icons";
import PromoDrawLayer from "../../layers/DrawerLayers/PromoDrawerLayer/PromoDrawLayer";
import getIcon from "../../utils/Icon";
import {useDispatch, useSelector} from "react-redux";
import {setSelectedPromoID} from "../../store/slices/selectedPromoIDSlice";
import {setPromoForm} from "../../store/slices/promoFormSlice";
import {openFullDrawer} from "../../store/slices/fullDrawerSlice";
import users_icon from '../../assets/icons/users.png';
import CoinBadge from "../coin_badge/CoinBadge";
import feeApi from "../../api/feeApi";
import referralApi from "../../api/referralApi";
import {Tooltip, message, Popover} from "antd";
import {formatNumberShort} from "../../utils/formatter";

const PromoCard = ({promo, isLayer}) => {
    const [fee, setFee] = useState(null);
    const [discount, setDiscount] = useState({discountPercentage: 0, isApplied: false});
    const [referralLink, setReferralLink] = useState(null);
    const [linkLoading, setLinkLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const dispatch = useDispatch();
    const selectedPromoID = useSelector((state) => state.selectedPromoID.selectedPromoID);
    const promoType = promo.promoType;

    // Вычисляем бюджет кристаллов из positions (для onlyCrystals)
    const totalCrystalBudget = promo?.category?.positions?.reduce((sum, p) => {
        const budget = Number(p?.budget);
        if (!Number.isNaN(budget) && budget > 0) {
            return sum + budget;
        }
        const winners = Number(p?.winners) || 0;
        const drop = Number(p?.dropPerWinner) || 0;
        return sum + (winners * drop);
    }, 0) || 0;

    // Получаем текущую комиссию
    useEffect(() => {
        const fetchFee = async () => {
            if (!promo?.id || !promo?.participantsCount) return;
            try {
                const result = promoType === 'onlyCrystals'
                    ? await feeApi.calculateFeeGem(promo.id, promo.participantsCount)
                    : await feeApi.calculateFee(promo.id, promo.participantsCount);
                setFee(result.toFixed(2));
            } catch (e) {
                console.error("Ошибка при получении комиссии:", e);
                setFee(null);
            }
        };
        fetchFee();
    }, [promo?.id, promo?.participantsCount, promoType]);

    // Получаем статус скидки для этого промо
    useEffect(() => {
        const fetchDiscount = async () => {
            if (!promo?.id) return;
            try {
                const {discountPercentage, isApplied} = await feeApi.getDiscountStatus(promo.id);
                setDiscount({discountPercentage, isApplied});
            } catch (e) {
                console.error("Ошибка при получении скидки:", e);
                setDiscount({discountPercentage: 0, isApplied: false});
            }
        };
        fetchDiscount();
    }, [promo?.id]);

    // Для рендера «камней» на карточке
    const stone = (value) => {
        if (!value) return null;
        const count = parseInt(value.toString().split(' ')[0], 10);
        if (count > 6) return getIcon('diamond');
        if (count >= 4) return getIcon('crown');
        if (count >= 1) return getIcon('star_big');
        return null;
    };

    // Иконка категории
    const renderIcon = () => {
        const count = parseInt((promo?.category?.category_name || '').split(' ')[0], 10);
        if (promoType === 'cryptoOnly') {
            let border = '', label = '';
            if (count > 6) {
                border = '#D240D2';
                label = '7';
            } else if (count >= 4) {
                border = '#47C5D5';
                label = '5';
            } else if (count >= 1) {
                border = '#FFB700';
                label = '3';
            }
            return (
                <div
                    className="w-7 h-7 flex items-center justify-center rounded-full text-white font-bold text-sm"
                    style={{border: `2px solid ${border}`, backgroundColor: 'transparent'}}
                >
                    {label}
                </div>
            );
        }
        return <img className="w-7 h-7" src={stone(promo?.category?.category_name)} alt="stone"/>;
    };

    // Стили для кнопки статуса
    const getStatusStyle = (status) => {
        switch (status) {
            case "ACTIVE":
                return "border border-emerald-400/35 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30";
            case "COMPLETED":
            case "MANUALLY_COMPLETED":
                return "border border-sky-400/35 bg-sky-500/20 text-sky-100 hover:bg-sky-500/30";
            case "PENDING":
                return "border border-rose-400/35 bg-rose-500/20 text-rose-100 hover:bg-rose-500/30";
            default:
                return "border border-white/15 bg-white/[0.08] text-white/80 hover:bg-white/[0.12]";
        }
    };


    // Получить реферальную ссылку
    const handleGetReferralLink = async (e) => {
        e?.stopPropagation();
        if (linkLoading) return;
        
        setLinkLoading(true);
        try {
            // Используем creatorUserId промо для создания ссылки
            const result = await referralApi.getPromoReferralLink(promo.creatorUserId, promo.id);
            const link = result?.link || result;
            setReferralLink(link);
        } catch (err) {
            console.error('[PromoCard] Failed to get referral link:', err);
            message.error('Failed to get referral link');
        } finally {
            setLinkLoading(false);
        }
    };

    // Копировать ссылку
    const handleCopyLink = (e) => {
        e?.stopPropagation();
        if (referralLink) {
            navigator.clipboard.writeText(referralLink);
            setCopied(true);
            message.success('Link copied!');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Открыть детальный drawer
    const promoDetail = (e) => {
        if (isLayer) return;
        e?.stopPropagation();
        dispatch(setSelectedPromoID(promo.id));
        dispatch(setPromoForm(promo));
        dispatch(openFullDrawer(<PromoDrawLayer create={false}/>));
    };

    return (
        <div
            onClick={promoDetail}
            className={`
        w-[340px] cursor-pointer rounded-xl border border-white/10 bg-white/[0.06] p-3 text-white shadow-[0_18px_50px_-28px_rgba(0,0,0,0.85)] backdrop-blur-xl transition-[box-shadow,border-color]
        flex flex-col gap-2
        ${promo.id === selectedPromoID ? 'border-emerald-400/45 ring-2 ring-emerald-400/35' : ''}
      `}
        >
            {/* Верхний блок: комиссия и тип промо */}
            <div className="flex flex-row gap-12 items-center">
                <div
                    className="relative inline-flex w-fit items-center rounded-lg border border-white/10 bg-white/[0.08] px-2 py-[2px] text-[12px] font-medium text-emerald-100">
                    ${fee ?? '...'}
                    {discount.isApplied && discount.discountPercentage > 0 && (
                        <span
                            className="absolute -right-5 -top-2 rounded border border-emerald-400/30 bg-emerald-500/25 px-1 text-[10px] font-medium text-emerald-100"
                        >
                 −{discount.discountPercentage}%
                          </span>
                    )}
                </div>

                <div
                    className="flex w-fit flex-row items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] px-2 py-[2px] text-[12px] font-sans text-white/80"
                >
                    {promoType === "onlyCrystals"
                        ? "Crystals only"
                        : promoType === "crystalsAndCrypto"
                            ? "Crypto and crystals"
                            : "Crypto only"
                    }
                </div>
            </div>

            {/* Средний блок: иконка, название и участники/призы */}
            <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center text-sm gap-2">
                    {renderIcon()}
                    {promo?.promoters?.[0]?.logo ? (
                        <img
                            src={`https://api.duotetra.quest${promo.promoters[0].logo}`}
                            alt="promoter logo"
                            className="w-5 h-5 rounded-full object-cover"
                        />
                    ) : (
                        <img
                            src={getIcon("dog")}
                            alt="default icon"
                            className="w-5 h-5"
                        />
                    )}
                    <span>
       <Tooltip title={promo?.promoters?.[0]?.name}>
    <span
        className="max-w-[80px] overflow-hidden text-ellipsis whitespace-nowrap"
        style={{display: 'inline-block'}}
    >
       {promo?.promoters?.[0]?.name?.length > 5 ? (
           <Tooltip title={promo.promoters[0].name}>
        <span className="max-w-[80px] whitespace-nowrap" style={{ display: 'inline-block' }}>
            {promo.promoters[0].name.slice(0, 5)}
        </span>
           </Tooltip>
       ) : (
           <span className="whitespace-nowrap">
        {promo?.promoters?.[0]?.name || "Neiro"}
    </span>
       )}

    </span>
</Tooltip>

    </span>
                </div>

                {promo.prizePlacesCount === promo.participantsCount ? (
                    <div className="flex flex-row items-center text-sm font-thin gap-1">
                        {formatNumberShort(promo.prizePlacesCount) }
                        <img className="w-[12px] h-[12px] animate-bounce" src={getIcon('cup')} alt="cup"/>

                        {promoType === 'onlyCrystals' ? (
                            <div className="h-[30px] rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 p-[1px]">
                                <div className="flex h-full w-full items-center justify-center gap-1 rounded-2xl bg-[#070A12]/90 px-2 backdrop-blur-sm">
                                    <span className="text-white text-[12px] font-medium">{formatNumberShort(totalCrystalBudget)}</span>
                                    <img src={stone(promo?.category?.category_name)} alt="crystal" className="w-[18px] h-[18px] object-contain"/>
                                    <span className="text-white text-[12px]">Crystals</span>
                                </div>
                            </div>
                        ) : (
                            <CoinBadge
                                name={promo.tokenType}
                                icon={promo.tokenType}
                                isCenter
                                color="#fff"
                                count={promo.totalAmount}
                            />
                        )}
                    </div>
                ) : (
                    <div className="flex flex-row items-center text-sm font-thin gap-1">
                        {formatNumberShort(promo.participantsCount) }
                        <img src={users_icon} className="w-4 h-3" alt="users"/>
                        {promo.prizePlacesCount}
                        <img className="w-[12px] h-[12px]" src={getIcon('cup')} alt="cup"/>
                        <div className={'w-max-[140px]'}>
                            {promoType === 'onlyCrystals' ? (
                                <div className="h-[30px] rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 p-[1px]">
                                    <div className="flex h-full w-full items-center justify-center gap-1 rounded-2xl bg-[#070A12]/90 px-2 backdrop-blur-sm">
                                        <span className="text-white text-[12px] font-medium">{formatNumberShort(totalCrystalBudget)}</span>
                                        <img src={stone(promo?.category?.category_name)} alt="crystal" className="w-[18px] h-[18px] object-contain"/>
                                        <span className="text-white text-[12px]">Crystals</span>
                                    </div>
                                </div>
                            ) : (
                                <CoinBadge
                                    name={promo.tokenType}
                                    icon={promo.tokenType}
                                    isCenter
                                    color="#fff"
                                    count={promo.totalAmount}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Нижний блок: кнопки загрузки и статус */}
            <div className="flex flex-row justify-end gap-6 border-t border-white/10 pt-1">
                <Popover
                    trigger="click"
                    placement="top"
                    onOpenChange={(open) => {
                        // При открытии Popover автоматически загружаем ссылку
                        if (open && !referralLink && !linkLoading) {
                            handleGetReferralLink();
                        }
                    }}
                    content={
                        <div className="flex min-w-[280px] flex-col gap-2" onClick={e => e.stopPropagation()}>
                            <div className="text-sm font-semibold text-white/90">Referral Link</div>
                            {linkLoading ? (
                                <div className="text-xs text-white/45">Loading...</div>
                            ) : referralLink ? (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        value={referralLink}
                                        readOnly
                                        className="flex-1 truncate rounded-lg border border-white/10 bg-white/[0.06] px-2 py-1 text-xs text-white/90"
                                    />
                                    <button
                                        onClick={handleCopyLink}
                                        className="flex items-center gap-1 rounded-lg border border-emerald-400/35 bg-emerald-500/20 px-2 py-1 text-xs text-emerald-100 hover:bg-emerald-500/30"
                                    >
                                        {copied ? <CheckOutlined /> : <CopyOutlined />}
                                        {copied ? 'Copied' : 'Copy'}
                                    </button>
                                </div>
                            ) : (
                                <div className="text-xs text-rose-300/90">Failed to load link</div>
                            )}
                        </div>
                    }
                    styles={{ body: { background: 'rgba(15, 20, 28, 0.96)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', padding: 12 } }}
                >
                    <button
                        onClick={e => e.stopPropagation()}
                        className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.06] px-3 py-1 text-xs text-white/90 transition hover:border-white/20 hover:bg-white/10"
                    >
                        <UploadOutlined />
                    </button>
                </Popover>
                <button
                    className={`rounded-md px-2 py-[5px] text-xs font-semibold transition-colors ${getStatusStyle(promo.status)}`}
                >
                    {promo.status === "ACTIVE"
                        ? "Live"
                        : promo.status === "COMPLETED" || promo.status === "MANUALLY_COMPLETED"
                            ? "Completed"
                            : "Pending"
                    }
                </button>

            </div>
        </div>
    );
};

export default PromoCard;
